// Lets overhaul this workerDash component. 
//We will use redux to manage state and fetch job sites. 
//we will also use reduc to manage worker location and handle logout functionality.
// When the redux state for location is set, we will fetch the jobsites and sort them by distance from the worker's location. 



import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { useEffect, useMemo, useRef } from 'react';
import { FiCalendar, FiLogOut } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import {getEmployerJobsites, resetEmployerJobsites} from '../../features/workerJobsites/employerJobsitesSlice';
import JobsiteCardContainer from '../../components/workerUser_Components/JobsiteCardContainer';
import { setWorkerLocation } from '../../features/workerAuth/workerAuthSlice';
import Spinner from '../../components/Spinner';
import {logoutWorker, resetWorkerUser} from '../../features/workerAuth/workerAuthSlice'
import { Navigate } from 'react-router-dom';
import { setJobsite, retrievePunchData } from '../../features/currentJobsite/currentJobsiteSlice';
import { getEmployerSubStatus } from '../../features/workerAuth/workerAuthSlice';

// Single-source distance calculation (Haversine). Distances & radius eligibility
// are computed once here and passed down; child components.
//calculates distance between two points
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const toRad = (v) => (v * Math.PI) / 180;
  const R = 6371; //radius od the earth in km 
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  return 2 * R * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)); // km
};

function WorkerDash() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const prevAccRef = useRef(Infinity);
   
  // Redux store states 
  const workerUser = useSelector((state) => state.workerAuth.workerUser);
  const profilePictureUrl = workerUser?.profilePictureUrl;

  const workerLocation = useSelector((state) => state.workerAuth.workerLocation);  // current worker location
  const currentJobsite = useSelector((state) => state.currentJobsite.jobsite);

  const {jobsites, isLoading, isError, message} = useSelector((state) => state.employerJobsites);

  // Get the last punch data jobsite ID from the state
  const lastPunchDataJobsiteId = useSelector(
  (s) => s.currentJobsite.lastPunchDataJobsiteId
  );
  
  

  // Derived augmented jobsite list (distance + radius eligibility) computed once per location/jobsites change
  const sortedJobSites = useMemo(() => {
    if (
      workerLocation &&
      workerLocation.latitude != null &&
      workerLocation.longitude != null &&
      jobsites &&
      jobsites.length > 0
    ) {
      const accuracyMeters = workerLocation.accuracy ?? null;

      return jobsites
        .map((jobsite) => {
          const distanceKm = calculateDistance(
            workerLocation.latitude,
            workerLocation.longitude,
            jobsite.latitude,
            jobsite.longitude
          );
          const distanceMeters = distanceKm * 1000;
          const radiusMeters = typeof jobsite.radius === 'number'
            ? jobsite.radius
            : parseFloat(jobsite.radius) || 0;

          // Conservative inclusion:
          // 1. Must be inside radius by raw distance
          // 2. If we have accuracy, require (distance + accuracy) not to wildly exceed radius
          //    Allow a small cushion factor (e.g. 1.15) to tolerate normal jitter.
          const cushionFactor = 1.15;
            // If no accuracy, fall back to simple check
          const conservativeWithin = radiusMeters > 0 && (
            (accuracyMeters == null && distanceMeters <= radiusMeters) ||
            (accuracyMeters != null &&
              distanceMeters <= radiusMeters &&                       // inside raw
              (distanceMeters + accuracyMeters) <= radiusMeters * cushionFactor // plausible certainty
            )
          );

          return {
            ...jobsite,
            distanceAway: distanceKm, // legacy km field
            distanceMeters,
            radiusMeters,
            accuracyMeters,
            distancePlusAccuracy: accuracyMeters != null ? distanceMeters + accuracyMeters : null,
            isWithinRadius: conservativeWithin,
          };
        })
        .sort((a, b) => a.distanceMeters - b.distanceMeters);
    }
    return [];
  }, [workerLocation, jobsites]);

  // Handle navigation to calendar
  const handleHistoryClick = () => {
    navigate(`/calendar`);
  };

  //handle logout click
   const handleLogoutClick = () => {
     dispatch(resetEmployerJobsites()) //resets the jobsites state to initial state
     dispatch(logoutWorker())     //removes the workerUser data from local storage
     dispatch(resetWorkerUser())  //resents the redux state to initial state for slice workerAuth
     
     navigate("/workerLogin")
  };

  // Check onboarding status
  useEffect(() => {
  if (workerUser?.onboarded === false) {
    toast.info('Complete your onboarding');
    navigate('/workerOnboarding');
    }
  }, [workerUser, navigate]);

  // Get the employer subscription status if the worker is onboarded 
  useEffect(() => {
  if (workerUser?.onboarded) {
    dispatch(getEmployerSubStatus());
    }
  }, [workerUser, dispatch]);


  

  // Fetch job sites and worker location when the component mounts.
  useEffect(() => {
     if (!workerUser || workerUser.onboarded === false) return;
 
     const options = {
       enableHighAccuracy: true,
       timeout: 10000,       // 10s to wait for a high-accuracy fix
       maximumAge: 10000,    // accept cached if <10s old
     };
 
     let watchId;
 
     const handleSuccess = (position) => {
       const { latitude, longitude, accuracy } = position.coords;
 
       // Always take first
      if (workerLocation.latitude == null || workerLocation.longitude == null) {
        dispatch(setWorkerLocation({ latitude, longitude, accuracy, ts: Date.now() }));
        prevAccRef.current = accuracy;
        return;
      }
 
       // Adaptive acceptance
       const MAX_COARSE = 250; // hard upper bound
      const improved = accuracy < prevAccRef.current;
       const acceptable = accuracy <= MAX_COARSE;
 
      if (improved || acceptable) {
        dispatch(setWorkerLocation({ latitude, longitude, accuracy, ts: Date.now() }));
        prevAccRef.current = accuracy;
      }
     };
 
     const getInitial = () => {
       navigator.geolocation.getCurrentPosition(handleSuccess, handleGeoError, options);
     };
 
     if (navigator.geolocation) {
       getInitial();
       // Continuous updates
       watchId = navigator.geolocation.watchPosition(handleSuccess, handleGeoError, {
         enableHighAccuracy: true,
         timeout: 15000,
         maximumAge: 5000,
       });
     } else {
       toast.error('Geolocation not supported.');
     }
 
     // Fetch jobsites once (keep as you had)
     dispatch(getEmployerJobsites());
 
     return () => {
      if (watchId) navigator.geolocation.clearWatch(watchId);
     };
   }, [workerUser, dispatch]);

  // Add a handler to retry getting location without reloading
  const handleRetryLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          };
          dispatch(setWorkerLocation(location));
        },
        (error) => {
          console.log(error);
          toast.error('Location access denied. Please enable location services in your browser settings.');
        }
      );
    } else {
      toast.error('Geolocation is not supported by this browser.');
    }
  };

  // Select current jobsite & fetch punch data once upon entering radius (prevents per-card loops)
  // when the worker enters the radius of a jobsite, you check if currentJobsite is either not set 
  // or is different from the new in-radius jobsite. If so, you dispatch actions to set the new current 
  // jobsite and fetch punch data:
  useEffect(() => {
    if (!sortedJobSites.length) return;
    const firstInRadius = sortedJobSites.find(js => js.isWithinRadius);
    if (firstInRadius && (!currentJobsite || currentJobsite.id !== firstInRadius.id)) {
      dispatch(setJobsite(firstInRadius));
     
      // dispatch(retrievePunchData(firstInRadius.id));
    }
  }, [sortedJobSites, currentJobsite, dispatch]);


  


  // Whenever currentJobsite changes, fetch punch data for that jobsite
  useEffect(() => {
    if (!currentJobsite) return;
    if (currentJobsite.id !== lastPunchDataJobsiteId) {
      dispatch(retrievePunchData(currentJobsite.id));
    }
  }, [currentJobsite, lastPunchDataJobsiteId, dispatch]);
  
  if (!workerUser) {
   return <Navigate to="/workerLogin" />;
  }
  // Render loading spinner
  
  if (isLoading) {
    return <Spinner />;
  }

  // Render error message
  if (isError) {
    return <div className="p-4 text-center text-critical">{message}</div>;
  }

  // Handle geolocation errors
  const handleGeoError = (error) => {
  switch (error.code) {
    case error.PERMISSION_DENIED:
      toast.error('Location permission denied. Please allow access in your browser or device settings.');
      break;
    // case error.POSITION_UNAVAILABLE:
    //   toast.error('Location information is unavailable.');
    //   break;
    case error.TIMEOUT:
      toast.error('Location request timed out. Try again.');
      break;
    default:
      toast.error('An unknown error occurred while fetching location.');
  }
  };

  return (
    <div className="min-h-screen bg-bg">
      <div className="mx-auto flex min-h-screen max-w-7xl flex-col gap-4 p-4 lg:flex-row lg:gap-6 lg:p-6">
      {/* Sidebar for large screens only */}
      <aside className="hidden md:flex md:w-72 md:shrink-0 md:flex-col md:rounded-2xl md:border md:border-border md:bg-surface md:p-5 md:shadow-sm lg:sticky lg:top-6 lg:h-fit">
        <img
          src={profilePictureUrl}
          alt={`${workerUser.workerName} ${workerUser.workerLastName}`}
          className="mx-auto h-24 w-24 rounded-full border border-border object-cover"
        />
        <p className="mt-4 text-center text-xl font-semibold text-ink">
          {workerUser.workerName} {workerUser.workerLastName}
        </p>
        <button
          className="mt-5 inline-flex items-center justify-center gap-2 rounded-md border border-border bg-surface px-4 py-2 text-label font-medium text-ink-2 transition hover:bg-surface-2"
          onClick={handleHistoryClick}
        >
          <FiCalendar />
          {workerUser.language === "Spa" ? "Historial de Trabajo" : "Work History"}
        </button>
      </aside>
      {/* Main content area */}
      <main className="flex-1 rounded-2xl border border-border bg-surface shadow-sm">
        {/* Worker info for small screens */}
        <div
          className="
            mb-2 flex w-full flex-col items-center justify-center border-b border-border px-4 py-6 md:hidden
            flex-col items-center justify-center
            flex-grow
            min-h-[30vh] max-h-[45vh]
          "
          style={{
            minHeight: 'clamp(160px, 30vh, 320px)',
            maxHeight: 'clamp(220px, 45vh, 400px)',
          }}
        >
          <div className="flex flex-col items-center flex-1 justify-center w-full">
            <img
              src={profilePictureUrl}
              alt={`${workerUser.workerName} ${workerUser.workerLastName}`}
              className="
                rounded-full
                transition-all
                mb-2
              "
              style={{
                width: 'clamp(112px, 32vw, 220px)',
                height: 'clamp(112px, 32vw, 220px)',
                minWidth: '112px',
                minHeight: '112px',
                maxWidth: '220px',
                maxHeight: '220px',
                objectFit: 'cover',
              }}
            />
            <span
              className="mt-2 text-center font-semibold text-ink"
              style={{
                fontSize: 'clamp(1.5rem, 2.5vw, 2rem)',
              }}
            >
              {workerUser.workerName} {workerUser.workerLastName}
            </span>
          </div>
          <div className="flex flex-col justify-center items-stretch w-full mt-4 space-y-2">
            <button
              className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-border bg-surface p-2 text-label font-medium text-ink-2 transition hover:bg-surface-2"
              onClick={handleHistoryClick}
              style={{
                fontSize: 'clamp(0.95rem, 2vw, 1.15rem)',
              }}
            >
              <FiCalendar className="h-4 w-4" />
              {workerUser.language === "Spa" ? "Historial de Trabajo" : "Work History"}
            </button>
            {/* Log Out button only on small screens */}
            <button
              className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-border bg-surface p-2 text-label font-medium text-ink-2 transition hover:bg-surface-2"
              onClick={handleLogoutClick}
              style={{
                fontSize: 'clamp(0.95rem, 2vw, 1.15rem)',
              }}
            >
              <FiLogOut className="h-4 w-4" />
              {workerUser.language === "Spa" ? "Salir" : "Log Out"}
            </button>
          </div>
        </div>

        {/* Job sites container */}
        <div className="flex flex-1 flex-col bg-bg p-3 sm:p-4 lg:h-screen lg:overflow-y-auto">
          {workerLocation?.latitude == null || workerLocation?.longitude == null ? (
            <div className="rounded-xl border border-border bg-surface p-6 text-center">
              <h2 className="mb-4 text-xl font-medium text-ink-2">
                Location is required to show nearby jobsites.<br />
                Turn On location services in your browser settings, then try again.
              </h2>
              <button
                className="rounded-md bg-ink px-4 py-2 text-label font-medium text-white transition hover:bg-ink-2"
                onClick={handleRetryLocation}
              >
                Try Again
              </button>
              
            </div>
          ) : sortedJobSites.length !== 0 ? (
            <div
              className="
                flex flex-col items-center gap-4 py-2
                overflow-y-auto
                max-h-[60vh] lg:max-h-[70vh]
                w-full
                md:grid md:grid-cols-2 lg:grid-cols-3 md:gap-6 md:overflow-y-auto
                scrollbar-thumb-slate-300 scrollbar-track-transparent
              "
            >
              {sortedJobSites.map((jobSite, idx) => (
                <div
                  key={jobSite.id}
                  className={`
                    w-full max-w-md mx-0 transition-transform duration-300 p-4 rounded-2xl border bg-surface shadow-sm mb-4 last:mb-0
                    ${jobSite.isWithinRadius ? 'border-active/30 bg-active/10' : 'border-border'}
                    ${idx === 0 ? 'z-10 md:z-0' : ''}
                  `}
                >
                  {/* Eligibility Badge */}
                  <div className="flex justify-between items-center mb-2">
                  
                    <span
                      className={`text-eyebrow px-2 py-0.5 rounded ${
                        jobSite.isWithinRadius
                          ? 'bg-active text-white'
                          : 'bg-surface-2 text-ink-3'
                      }`}
                    >
                      {jobSite.isWithinRadius
                        ? (workerUser.language === 'Spa' ? 'Dentro del radio' : 'In Radius')
                        : (workerUser.language === 'Spa' ? 'Fuera de radio' : 'Out of Radius')}
                    </span>
                  </div>
                  <JobsiteCardContainer jobSite={jobSite} />
                </div>
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-border bg-surface p-6 text-center">
              <h2 className="text-2xl text-ink-3">
                Employer doesn't have any jobsites
              </h2>
            </div>
          )}
        </div>
      </main>
      </div>
    </div>
  );
}

export default WorkerDash;
